// src/utils/guardiaSalida.js
// "No pierdas lo que capturaste" (etapa 3b del enrutador): una pantalla con
// datos sin guardar se protege con
//
//   const liberar = useGuardiaSalida(hayCambios, 'Mensaje de confirmación');
//
// Mientras `hayCambios` sea true, salir de la dirección en la que se activó
// pregunta antes: por el menú (App.js → puedeSalir), con atrás/adelante (App.js
// sigue pintando la dirección protegida mientras pregunta y, si se cancela,
// deshace el salto) y al recargar o cerrar (aviso propio del navegador).
//
// La pantalla que sale A PROPÓSITO después de guardar, o de que el usuario ya
// confirmó en su propio botón («← Volver»), llama `liberar()` justo antes de
// cerrar: así no se pregunta dos veces.
//
// Una sola guardia a la vez (cada pantalla protege su propia dirección). Nueva
// orden y el Ingreso de resultados usan esta misma pieza.
import { useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

// La ruta se guarda tal cual (un correlativo como DEMO-F01 conserva sus
// mayúsculas); solo la comparación ignora mayúsculas y la barra final.
const sinBarra = (p) => (p || '/').replace(/\/+$/, '') || '/';
const normalizar = (p) => sinBarra(p).toLowerCase();

const guardias = new Map();   // id → { activa, liberada, ruta, mensaje }
let siguienteId = 1;

// La guardia que manda ahora, o null: { ruta, mensaje }.
export function guardiaVigente() {
  for (const g of guardias.values()) {
    if (g.activa && !g.liberada && g.ruta) return { ruta: g.ruta, mensaje: g.mensaje };
  }
  return null;
}

// ¿Ir a `destino` (pathname) sale de la dirección protegida?
export function saleDeGuardia(destino) {
  const g = guardiaVigente();
  return !!g && normalizar(destino) !== normalizar(g.ruta);
}

// Menú y botones internos: true si se puede ir a `destino`.
export function puedeSalirA(destino) {
  const g = guardiaVigente();
  if (!g || normalizar(destino) === normalizar(g.ruta)) return true;
  return window.confirm(g.mensaje);
}

export function useGuardiaSalida(activa, mensaje) {
  const location = useLocation();
  const idRef = useRef(null);
  if (idRef.current == null) idRef.current = siguienteId++;
  const rutaRef = useRef(location.pathname);
  // La dirección protegida es la que había cuando aparecieron los cambios.
  // Mientras siguen, no se mueve (en un "atrás" pendiente de confirmar, la
  // dirección real ya es otra).
  if (!activa) rutaRef.current = location.pathname;

  const id = idRef.current;
  const previa = guardias.get(id);
  guardias.set(id, {
    activa: !!activa,
    liberada: activa ? !!(previa?.activa && previa.liberada) : false,
    ruta: sinBarra(activa && previa?.activa ? previa.ruta : rutaRef.current),
    mensaje,
  });

  useEffect(() => () => { guardias.delete(id); }, [id]);

  // Recargar o cerrar la pestaña: el aviso propio del navegador.
  useEffect(() => {
    if (!activa) return undefined;
    const avisar = (e) => {
      if (guardias.get(id)?.liberada) return;
      e.preventDefault(); e.returnValue = '';
    };
    window.addEventListener('beforeunload', avisar);
    return () => window.removeEventListener('beforeunload', avisar);
  }, [activa, id]);

  return useCallback(() => {
    const g = guardias.get(id);
    if (g) g.liberada = true;
  }, [id]);
}
